import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SLOTS, to12h } from "@/lib/slots";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { CheckCircle2, HeartHandshake } from "lucide-react";

const todayStr = () => new Date().toISOString().split("T")[0];

export default function BookAppointment() {
  const [form, setForm] = useState({
    full_name: "", date_of_birth: "", dental_concerns: "", has_insurance: "yes",
    insurance_provider: "", insurance_member_id: "", email: "", phone: "",
    appointment_date: "", appointment_time: "",
  });
  const [taken, setTaken] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [blocks, setBlocks] = useState([]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!form.appointment_date) return;
    api
      .get(`/appointments/slots?appointment_date=${form.appointment_date}`)
      .then((r) => setTaken(r.data.map((d) => d.appointment_time)))
      .catch(() => setTaken([]));
    api
      .get(`/blocks?appointment_date=${form.appointment_date}`)
      .then((r) => setBlocks(r.data))
      .catch(() => setBlocks([]));
  }, [form.appointment_date]);

  const dayClosed = blocks.some((b) => b.all_day);
  const blockedTimes = blocks.filter((b) => !b.all_day).map((b) => b.time);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.appointment_time) return toast.error("Please select a time slot.");
    setSubmitting(true);
    try {
      const payload = { ...form, has_insurance: form.has_insurance === "yes" };
      const { data } = await api.post("/appointments", payload);
      toast.success("Request sent — pending confirmation");
      setDone({ needsMobile: data.needs_mobile_care });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Booking failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="App">
        <Navbar />
        <section className="max-w-2xl mx-auto px-6 pt-40 pb-32 text-center">
          <CheckCircle2 className="h-16 w-16 text-[#3E7B5A] mx-auto" />
          <h1 className="font-serif text-4xl text-[#2D2336] mt-6">Appointment Request Sent</h1>
          <p className="text-lg text-[#5C5166] mt-4">
            Thank you, {form.full_name.split(" ")[0]}. Your request has been <strong>sent but is not
            confirmed yet</strong>. Our team will review it and reach out to confirm, reschedule, or
            follow up with you shortly.
          </p>
          {done.needsMobile && (
            <div className="mt-8 bg-[#E5DEEB] border border-[#C8B3D6] rounded-2xl p-8 text-left" data-testid="mobile-care-notice">
              <HeartHandshake className="h-8 w-8 text-[#6B4C8A]" />
              <h3 className="font-serif text-2xl text-[#2D2336] mt-3">Utopia Mobile Care will contact you</h3>
              <p className="text-[#5C5166] mt-2 leading-relaxed">
                Since you don't currently have insurance, our 501(c)(3) nonprofit,
                Utopia Mobile Care, will be in contact with you using the information
                you submitted to help you access affordable care.
              </p>
            </div>
          )}
          <a href="/" className="inline-block mt-10 text-[#6B4C8A] font-medium hover:underline">← Back to Home</a>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Navbar />
      <section className="max-w-3xl mx-auto px-6 pt-32 pb-24">
        <p className="label text-sm uppercase tracking-[0.15em] text-[#78698A] font-semibold">Schedule a Visit</p>
        <h1 className="font-serif text-5xl text-[#2D2336] mt-3">Book an Appointment</h1>
        <p className="text-lg text-[#5C5166] mt-4">Tell us about you and choose a time. Hours are by appointment.</p>

        <form onSubmit={submit} className="mt-10 space-y-8" data-testid="appointment-form">
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <Label className="text-[#2D2336]">Full Name *</Label>
              <Input required data-testid="input-full-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="mt-2" />
            </div>
            <div>
              <Label className="text-[#2D2336]">Date of Birth *</Label>
              <Input required type="date" data-testid="input-dob" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} className="mt-2" />
            </div>
            <div>
              <Label className="text-[#2D2336]">Email</Label>
              <Input type="email" data-testid="input-email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-2" />
            </div>
            <div>
              <Label className="text-[#2D2336]">Phone</Label>
              <Input data-testid="input-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-2" />
            </div>
          </div>

          <div>
            <Label className="text-[#2D2336]">What dental concerns do you have? *</Label>
            <Textarea required data-testid="input-concerns" rows={4} value={form.dental_concerns} onChange={(e) => set("dental_concerns", e.target.value)} className="mt-2" placeholder="e.g. cleaning, tooth pain, checkup for my child..." />
          </div>

          <div className="bg-white border border-[#E5DEEB] rounded-2xl p-6">
            <Label className="text-[#2D2336]">Do you have dental insurance? *</Label>
            <RadioGroup value={form.has_insurance} onValueChange={(v) => set("has_insurance", v)} className="flex gap-6 mt-3" data-testid="insurance-radio">
              <label className="flex items-center gap-2 cursor-pointer">
                <RadioGroupItem value="yes" id="ins-yes" data-testid="insurance-yes" /> Yes
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <RadioGroupItem value="no" id="ins-no" data-testid="insurance-no" /> No
              </label>
            </RadioGroup>

            {form.has_insurance === "yes" ? (
              <div className="grid sm:grid-cols-2 gap-6 mt-5">
                <div>
                  <Label className="text-[#2D2336]">Insurance Provider</Label>
                  <Input data-testid="input-ins-provider" value={form.insurance_provider} onChange={(e) => set("insurance_provider", e.target.value)} className="mt-2" />
                </div>
                <div>
                  <Label className="text-[#2D2336]">Member / Policy ID</Label>
                  <Input data-testid="input-ins-member" value={form.insurance_member_id} onChange={(e) => set("insurance_member_id", e.target.value)} className="mt-2" />
                </div>
              </div>
            ) : (
              <div className="mt-5 bg-[#F3EFF7] border border-[#C8B3D6] rounded-xl p-5" data-testid="no-insurance-message">
                <p className="text-[#5C5166] leading-relaxed">
                  <strong className="text-[#6B4C8A]">No insurance? We're here for you.</strong> Once you
                  submit, <strong>Utopia Mobile Care (501c3)</strong> will be in contact with you using the
                  details you provide to help you access affordable dental care.
                </p>
              </div>
            )}
          </div>

          <div>
            <Label className="text-[#2D2336]">Preferred Date *</Label>
            <Input required type="date" min={todayStr()} data-testid="input-appt-date" value={form.appointment_date} onChange={(e) => { set("appointment_date", e.target.value); set("appointment_time", ""); }} className="mt-2" />
          </div>

          {form.appointment_date && (
            <div>
              <Label className="text-[#2D2336]">Choose a Time Slot *</Label>
              {dayClosed && (
                <div className="mt-2 bg-[#F6E2E2] border border-[#E4C4C4] rounded-xl p-4 text-sm text-[#964545]" data-testid="day-closed-notice">
                  We're not available on this date. Please choose another day.
                </div>
              )}
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-3" data-testid="slot-grid">
                {SLOTS.map((s) => {
                  const isTaken = taken.includes(s) || dayClosed || blockedTimes.includes(s);
                  const selected = form.appointment_time === s;
                  return (
                    <button
                      type="button"
                      key={s}
                      disabled={isTaken}
                      data-testid={`slot-${s}`}
                      onClick={() => set("appointment_time", s)}
                      className={`py-2.5 rounded-full text-sm font-medium border transition-all ${
                        isTaken
                          ? "bg-[#F1EEF4] text-[#B0A6BC] border-[#E5DEEB] cursor-not-allowed line-through"
                          : selected
                          ? "bg-[#6B4C8A] text-white border-[#6B4C8A]"
                          : "bg-white text-[#5C5166] border-[#E5DEEB] hover:border-[#6B4C8A] hover:text-[#6B4C8A]"
                      }`}
                    >
                      {to12h(s)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="appointment-submit"
            className="w-full bg-[#6B4C8A] text-white py-4 rounded-full font-medium hover:bg-[#543C6E] transition-all hover:-translate-y-0.5 shadow-sm disabled:opacity-60"
          >
            {submitting ? "Submitting..." : "Request Appointment"}
          </button>
          <p className="text-center text-sm text-[#78698A]">
            Submitting sends a request — your appointment is <strong>not confirmed</strong> until our team reaches out.
          </p>
        </form>
      </section>
      <Footer />
    </div>
  );
}
